import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoCard } from "@/components/toggles";
import { useAuth } from "@/contexts/AuthContext";
import { STXThemeScope } from "@/contexts/STXThemeContext";
import api, { formatError } from "@/lib/api";
import { useCatalogLabels } from "@/i18n/catalog";
import { StreetAutocomplete } from "@/components/address/StreetAutocomplete";
import { useCityAutofill } from "@/lib/address";
import { CreditCard, Building2, ArrowLeft, ShoppingBag, Check, AlertTriangle, Loader2, Copy, MapPin, Package, Sparkles, User as UserIcon, Timer } from "lucide-react";

export default function CheckoutPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const listingId = params.get("listing");
  const mysteryReservationId = params.get("mystery_reservation");
  const area = params.get("area") || (mysteryReservationId ? "STILLTOUCHEDX" : "MARKETPLACE");
  // iter88.22: STX Light/Dark Theme respektieren (Owner-Feedback 2026-07-17):
  // Wenn User im STX-Bereich manuell auf Light umschaltet, muss Checkout mitziehen.
  const { addon: addonLabel } = useCatalogLabels();
  const [addonLabels, setAddonLabels] = useState({});
  // iter88.25 · Pre-Sale-Verhandlungs-Kontext (Sonderwunsch → Checkout)
  const negotiationId = params.get("negotiation_id");
  const presaleId = params.get("presale_id");
  const [negotiation, setNegotiation] = useState(null);
  // iter88.15c: STX-Extras aus URL übernehmen (Direktkauf-Pfad)
  const urlAddons = (params.get("addons") || "").split(",").filter(Boolean);
  const urlWearDays = params.get("wear_days") ? parseInt(params.get("wear_days"), 10) : null;
  const isGuest = !user || user === false;
  const isMystery = Boolean(mysteryReservationId);

  const [listing, setListing] = useState(null);
  const [reservation, setReservation] = useState(null);
  const [remainingSec, setRemainingSec] = useState(0);
  const [methods, setMethods] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [savedPayments, setSavedPayments] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [loading, setLoading] = useState(true);

  // Sektion 1: Klarname (Vor- + Nachname) — NIE der Username/display_name
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const buyerName = `${firstName} ${lastName}`.trim();
  const [buyerEmail, setBuyerEmail] = useState("");
  // Sektion 2: Rechnungsadresse
  const [billStreet, setBillStreet] = useState("");
  const [billHouseNr, setBillHouseNr] = useState("");
  const [billZip, setBillZip] = useState("");
  const [billCity, setBillCity] = useState("");
  // Sektion 3: Lieferadresse
  const [deliveryMode, setDeliveryMode] = useState("SAME_AS_ADDRESS");
  const [shipName, setShipName] = useState("");
  const [shipStreet, setShipStreet] = useState("");
  const [shipHouseNr, setShipHouseNr] = useState("");
  const [shipZip, setShipZip] = useState("");
  const [shipCity, setShipCity] = useState("");
  const [packNr, setPackNr] = useState("");
  const [postNr, setPostNr] = useState("");
  const [packZip, setPackZip] = useState("");
  const [packCity, setPackCity] = useState("");
  // Sektion 4: Zahlungsart
  const [selectedMethod, setSelectedMethod] = useState(null);
  // Sektion 5+6
  const [order, setOrder] = useState(null);
  const [bankInstructions, setBankInstructions] = useState(null);
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);
  // iter79 Batch 2 M4: Buyer wählt optional ein Versandlabel im Checkout und zahlt es mit.
  const [labelCatalog, setLabelCatalog] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState(null);
  // Adress-Autovervollständigung (OpenPLZ) — Land aus dem Profil, sonst DE.
  const buyerCountry = (user && user !== false && user.profile?.address_country) || "DE";
  useCityAutofill(buyerCountry, billZip, billCity, setBillCity);
  useCityAutofill(buyerCountry, packZip, packCity, setPackCity);
  useCityAutofill(buyerCountry, shipZip, shipCity, setShipCity);
  // iter79 Batch 3: Käuferschutz 2,49 € (nur MP-Artikel)
  const [buyerProtectionOptin, setBuyerProtectionOptin] = useState(false);

  useEffect(() => {
    if (!listingId && !mysteryReservationId) { navigate(-1); return; }
    if (mysteryReservationId && isGuest) { navigate("/login"); return; }
    (async () => {
      try {
        const calls = [api.get("/payments/methods")];
        if (isMystery) {
          calls.unshift(api.get(`/rescue/wundertueten/reservation/${mysteryReservationId}`));
        } else {
          calls.unshift(api.get(`/listings/${listingId}`));
        }
        if (!isGuest) {
          calls.push(api.get("/buyer/addresses"), api.get("/buyer/payment-methods"), api.get("/buyer/checkout-preferences"));
        }
        const results = await Promise.all(calls);
        if (isMystery) {
          setReservation(results[0].data);
        } else {
          setListing(results[0].data);
        }
        setMethods(results[1].data.methods || []);
        if (!isGuest && results[2]) {
          setSavedAddresses(results[2].data.addresses || []);
          setSavedPayments(results[3].data.payment_methods || []);
          const p = results[4].data || {};
          setPrefs(p);
          if (p.default_payment_type) setSelectedMethod(p.default_payment_type);
          if (p.default_delivery_mode) setDeliveryMode(p.default_delivery_mode);
        }
        if (!isGuest && user) {
          setBuyerEmail(user.email || "");
          // Autofill personal + billing data for returning users so they
          // don't have to re-enter what we already have.
          const prof = user.profile || {};
          const savedList = (!isGuest && results[2]) ? (results[2].data.addresses || []) : [];
          const defaultAddr = savedList.find((a) => a.is_default && a.address_kind === "STANDARD") || savedList[0];
          // Immer den Klarnamen (Vor-/Nachname) vorbelegen — NIEMALS den Username/display_name.
          const addrNameParts = (defaultAddr?.name || "").trim().split(/\s+/).filter(Boolean);
          const fn = prof.first_name || defaultAddr?.first_name || (addrNameParts.length > 1 ? addrNameParts.slice(0, -1).join(" ") : "");
          const ln = prof.last_name || defaultAddr?.last_name || (addrNameParts.length > 1 ? addrNameParts[addrNameParts.length - 1] : "");
          setFirstName(fn);
          setLastName(ln);
          setBillStreet(defaultAddr?.street || prof.address_street || "");
          setBillHouseNr(defaultAddr?.house_number || prof.address_house_number || "");
          setBillZip(defaultAddr?.postal_code || prof.address_zip || "");
          setBillCity(defaultAddr?.city || prof.address_city || "");
        }
      } catch { navigate(-1); }
      finally { setLoading(false); }
    })();
  }, [listingId, mysteryReservationId, isMystery, navigate, isGuest, user]);

  // STX-Zusatz-Labels (DE) laden — sonst würden nur die Rohcodes angezeigt.
  useEffect(() => {
    if (area !== "STILLTOUCHEDX") return;
    (async () => {
      try {
        const { data } = await api.get("/stilltouchedx/catalog/addons");
        const map = {};
        for (const a of (data.addons || [])) map[a.code] = a.label;
        setAddonLabels(map);
      } catch { /* Fallback auf Roh-Code */ }
    })();
  }, [area]);

  // iter88.25/.33 · Pre-Sale/Verhandlungs-Kontext laden. Zwei Quellen:
  //   - MP-Verhandlung mit ?negotiation_id=…  (mp_negotiations, buyer_message)
  //   - STX-Presale mit ?presale_id=…         (presale_requests, item_description)
  useEffect(() => {
    if (negotiationId) {
      (async () => {
        try {
          const { data } = await api.get(`/marketplace/negotiations/${negotiationId}`);
          setNegotiation(data);
        } catch { /* silent */ }
      })();
      return;
    }
    if (presaleId) {
      (async () => {
        try {
          const { data } = await api.get(`/modul-d/presale/${presaleId}`);
          setNegotiation({
            buyer_message: data.item_description || "",
            final_price: data.counter_price || data.modified_price || data.price,
            accepted_at: data.accepted_at || data.updated_at,
          });
        } catch { /* silent */ }
      })();
    }
  }, [negotiationId, presaleId]);

  // iter79 Batch 2 M4: Load label catalog (nur bei MP-Fixed-Price, kein Wundertüte)
  useEffect(() => {
    if (isMystery) return;
    (async () => {
      try {
        const { data } = await api.get("/shipping/label-catalog");
        setLabelCatalog(data.carriers || []);
      } catch { /* ignore */ }
    })();
  }, [isMystery]);

  // Countdown timer for mystery reservation
  useEffect(() => {
    if (!reservation?.expires_at) return;
    const tick = () => {
      const remaining = Math.max(0, Math.floor((new Date(reservation.expires_at).getTime() - Date.now()) / 1000));
      setRemainingSec(remaining);
      if (remaining === 0) {
        setError(t("checkout_extra.reservation_expired"));
      }
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [reservation]);

  const getShippingData = () => {
    if (deliveryMode === "SAME_AS_ADDRESS") {
      return { shipping_name: buyerName, shipping_address: `${billStreet} ${billHouseNr}`.trim(), shipping_zip: billZip, shipping_city: billCity };
    } else if (deliveryMode === "PACKSTATION") {
      return { shipping_name: buyerName, shipping_zip: packZip, shipping_city: packCity, packstation_number: packNr, post_number: postNr };
    } else {
      return { shipping_name: buyerName, shipping_address: `${shipStreet} ${shipHouseNr}`.trim(), shipping_zip: shipZip, shipping_city: shipCity };
    }
  };

  const canSubmit = () => {
    if (!firstName.trim() || !lastName.trim() || !billStreet || !billHouseNr || !billZip || !billCity || !selectedMethod) return false;
    if (isGuest && !buyerEmail) return false;
    if (deliveryMode === "PACKSTATION" && (!packNr || !postNr || !packZip || !packCity)) return false;
    if (deliveryMode === "OTHER_DELIVERY_ADDRESS" && (!shipStreet || !shipHouseNr || !shipZip || !shipCity)) return false;
    return true;
  };

  const submitOrder = async () => {
    if (!canSubmit() || processing) return;
    setError(""); setProcessing(true);
    try {
      const shipping = getShippingData();
      const orderPayload = {
        payment_method: selectedMethod,
        delivery_mode: deliveryMode,
        guest_email: isGuest ? buyerEmail : undefined,
        shipping_name: shipping.shipping_name,
        billing_street: `${billStreet} ${billHouseNr}`.trim(),
        billing_zip: billZip,
        billing_city: billCity,
        shipping_address: shipping.shipping_address || "",
        shipping_zip: shipping.shipping_zip,
        shipping_city: shipping.shipping_city,
        packstation_number: shipping.packstation_number || "",
        post_number: shipping.post_number || "",
        save_as_default: false,
        // iter79 Batch 2 M4: Optionaler Labelkauf
        label_prepurchase_carrier: selectedLabel?.carrier || undefined,
        label_prepurchase_cost: selectedLabel?.price || 0,
        // iter79 Batch 3: MP-Käuferschutz (nur bei MP-Artikeln möglich)
        buyer_protection_optin: !isMystery && (listing?.area_scope === "MARKETPLACE") ? buyerProtectionOptin : false,
        area_scope: listing?.area_scope,
        idempotency_key: `${listingId || mysteryReservationId}_${Date.now()}`,
      };
      if (isMystery) orderPayload.mystery_reservation_id = mysteryReservationId;
      else {
        orderPayload.listing_id = listingId;
        // iter88.15c: STX-Extras beim Direktkauf mitschicken
        if (urlAddons.length > 0) orderPayload.selected_addon_codes = urlAddons;
        if (urlWearDays !== null && !Number.isNaN(urlWearDays)) orderPayload.selected_wear_days = urlWearDays;
        // iter88.19: Discount-Code
        if (appliedDiscount) orderPayload.discount_code = appliedDiscount.code;
        // iter88.25 · Sonderwunsch aus MP-Verhandlung ans Order-Backend weiterreichen
        if (negotiationId) {
          orderPayload.negotiation_id = negotiationId;
          const msg = negotiation?.buyer_message || negotiation?.seller_message;
          if (msg) orderPayload.special_request = msg;
          if (negotiation?.final_price != null) orderPayload.negotiated_price = negotiation.final_price;
        }
        if (presaleId) {
          orderPayload.presale_id = presaleId;
          const msg = negotiation?.buyer_message; // ist bereits item_description
          if (msg) orderPayload.special_request = msg;
          if (negotiation?.final_price != null) orderPayload.negotiated_price = negotiation.final_price;
        }
      }
      const { data } = await api.post("/checkout/create-order", orderPayload);
      setOrder(data);
      if (selectedMethod === "PAYPAL") {
        const frontendUrl = window.location.origin;
        const { data: ppData } = await api.post("/payments/paypal/create", { order_id: data.order_id, frontend_url: frontendUrl });
        if (ppData.approval_url) { window.location.href = ppData.approval_url; return; }
        setError("PayPal-Weiterleitung fehlgeschlagen");
      } else if (selectedMethod === "STRIPE") {
        // iter80: Stripe Hosted Checkout — Redirect zu externer Stripe-Page
        const originUrl = window.location.origin;
        const { data: sData } = await api.post("/payments/stripe/create", {
          order_id: data.order_id, origin_url: originUrl,
        });
        if (sData.url) { window.location.href = sData.url; return; }
        setError("Stripe-Weiterleitung fehlgeschlagen");
      } else if (selectedMethod === "BANK_TRANSFER") {
        const { data: bankData } = await api.post("/payments/bank-transfer/create", { order_id: data.order_id });
        setBankInstructions(bankData.instructions);
      }
    } catch (err) { setError(formatError(err)); }
    finally { setProcessing(false); }
  };

  // iter · Checkout ist IMMER im Light-Mode (Owner-Vorgabe 2026-06), auch aus dem
  // stilltouchedX-Dark-Bereich heraus. Das STX-Dark-Theme darf hier nicht durchschlagen.
  const isDark = false;
  // iter88.15c: STX-Extras clientseitig aus URL + Listing berechnen für Anzeige & Gesamtpreis
  const stxAddonRows = !listing || area !== "STILLTOUCHEDX" ? [] : (
    (listing.stx_addon_offers || [])
      .filter((o) => urlAddons.includes(o.code))
      .map((o) => ({ code: o.code, price: Number(o.price) || 0 }))
  );
  const stxWearRow = !listing || area !== "STILLTOUCHEDX" || urlWearDays === null || Number.isNaN(urlWearDays)
    ? null
    : ((listing.stx_extra_wear_offers || []).find((o) => Number(o.days) === urlWearDays) || null);
  const extrasAmount = stxAddonRows.reduce((s, a) => s + a.price, 0) + (stxWearRow ? Number(stxWearRow.price) || 0 : 0);
  // iter88.18b: STX-Versandkosten aus Admin-Config live holen
  const [shippingQuote, setShippingQuote] = useState(null);
  useEffect(() => {
    if (area !== "STILLTOUCHEDX" || !listing) { setShippingQuote(null); return; }
    (async () => {
      try {
        const cartAmt = Number(listing.price || 0) + extrasAmount;
        const params = new URLSearchParams({
          category: listing.category_code || "",
          item_count: "1",
          cart_amount_eur: String(cartAmt),
        });
        const { data } = await api.get(`/stx/shipping-quote?${params.toString()}`);
        setShippingQuote(data);
      } catch { setShippingQuote({ price_eur: 0 }); }
    })();
  }, [area, listing?.listing_id, listing?.category_code, extrasAmount, listing?.price]);
  const stxShippingCost = area === "STILLTOUCHEDX" ? Number(shippingQuote?.price_eur || 0) : 0;
  // iter88.19: Discount-Code
  const [discountInput, setDiscountInput] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountError, setDiscountError] = useState("");
  const [checkingDiscount, setCheckingDiscount] = useState(false);
  const applyDiscount = async () => {
    if (!discountInput.trim()) return;
    setCheckingDiscount(true); setDiscountError("");
    try {
      const cartAmt = Number(listing?.price || 0) + extrasAmount;
      const { data } = await api.post("/discount/redeem", {
        code: discountInput.trim().toUpperCase(),
        cart_amount_eur: cartAmt,
        area: listing?.area_scope || area,
        seller_id: listing?.seller_profile_id || null,
      });
      setAppliedDiscount(data);
    } catch (e) {
      setDiscountError(e.response?.data?.detail || "Code ungültig");
      setAppliedDiscount(null);
    } finally { setCheckingDiscount(false); }
  };
  const discountAmount = Number(appliedDiscount?.amount_off_eur || 0);
  const bg = isDark ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A]";
  const border = isDark ? "border-[#333]" : "border-[#E5E5E5]";
  const inputBase = `h-10 px-3 border text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] ${isDark ? "bg-[#1A1A1A] border-[#333] text-white placeholder-[#666]" : "bg-white border-[#E5E5E5] placeholder-[#BBB]"}`;
  const inputFull = `w-full ${inputBase}`;
  const labelCls = `text-[10px] font-bold uppercase tracking-wider mb-1.5 block ${isDark ? "text-[#888]" : "text-[#999]"}`;
  const sectionCls = `border-b ${border} px-4 py-5`;

  if (loading) return <div className={`min-h-full ${bg} flex justify-center py-16`}><Loader2 className="animate-spin" /></div>;

  // ─── CONFIRMATION ────────────────────────────────────
  if (order && (selectedMethod === "BANK_TRANSFER" || order.payment_status !== "CREATED")) {
    return (
      <div data-testid="checkout-confirmation" className={`min-h-full ${bg} pb-24`}>
        <div className={`px-4 py-3 border-b ${border} flex items-center gap-3`}>
          <button onClick={() => navigate(area === "STILLTOUCHEDX" ? "/stilltouchedx" : "/marketplace")}><ArrowLeft size={20} /></button>
          <h1 className="text-base font-bold">{t("checkout_extra.order_confirmation")}</h1>
        </div>
        <div className="px-4 py-6 max-w-md mx-auto space-y-4">
          <div className="text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-3"><Check size={28} className="text-green-600" /></div>
            <h2 className="text-lg font-bold">{t("checkout_extra.order_placed")}</h2>
            <p className={`text-sm mt-1 ${isDark ? "text-[#888]" : "text-[#999]"}`}>Nr. <span className="font-mono">{order.order_id}</span></p>
          </div>
          {/* Sektion 5: Bestellübersicht */}
          <div className={`border ${border} p-4`}>
            <p className="text-sm font-medium">{order.title}</p>
            <p className="text-xl font-bold mt-1">{order.price?.toFixed(2)} {order.currency}</p>
            <div className={`flex gap-2 mt-2 text-xs ${isDark ? "text-[#888]" : "text-[#999]"}`}>
              <span>{order.payment_method === "PAYPAL" ? "PayPal" : order.payment_method === "STRIPE" ? "Kreditkarte" : "Überweisung"}</span>
              <span>·</span>
              <span>{order.delivery_mode === "PACKSTATION" ? "Packstation" : order.delivery_mode === "OTHER_DELIVERY_ADDRESS" ? "Alternative Adresse" : "Standardadresse"}</span>
            </div>
            {Array.isArray(order.mystery_items) && order.mystery_items.length > 0 && (
              <div data-testid="confirmation-mystery-items" className="mt-3 pt-3 border-t border-[#E5E5E5]/50 space-y-1.5">
                <p className={labelCls}>{t("checkout_extra.included_items")}</p>
                {order.mystery_items.map((it, i) => (
                  <div key={it.item_id} data-testid={`confirmation-mystery-item-${i}`} className="flex items-center gap-2 text-[11px]">
                    <div className="flex-1 min-w-0 truncate">
                      {it.wear_duration_days}T · <span
                          data-testid={`confirmation-mystery-seller-${i}`}
                          className={isDark ? "text-[#888]" : "text-[#999]"}
                        >{it.seller_display_name || "anonym"}</span>
                    </div>
                    <span className={`font-mono ${isDark ? "text-[#888]" : "text-[#999]"}`}>{Number(it.rescue_price || 0).toFixed(2)} €</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Lieferadresse */}
          <div className={`border ${border} p-4`}>
            <p className={labelCls}>Lieferadresse</p>
            <p className="text-sm">{order.shipping_name || order.buyer_name}</p>
            {order.packstation_number ? (
              <><p className="text-sm">Packstation {order.packstation_number}</p><p className="text-sm">{order.shipping_zip} {order.shipping_city}</p></>
            ) : (
              <><p className="text-sm">{order.shipping_address || order.billing_street}</p><p className="text-sm">{order.shipping_zip || order.billing_zip} {order.shipping_city || order.billing_city}</p></>
            )}
          </div>
          {/* Bank Transfer Instructions */}
          {bankInstructions && (
            <div data-testid="bank-instructions" className={`border-2 p-4 ${isDark ? "border-amber-700 bg-amber-900/20" : "border-amber-300 bg-amber-50"}`}>
              <p className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? "text-amber-400" : "text-amber-700"}`}>{t("checkout_extra.transfer_data")}</p>
              <div className="space-y-2 text-sm">
                {[["Empfänger", bankInstructions.account_holder], ["IBAN", bankInstructions.iban, true], ["BIC", bankInstructions.bic], ["Bank", bankInstructions.bank_name], ["Betrag", `${bankInstructions.amount} ${bankInstructions.currency}`], ["Verwendungszweck", bankInstructions.reference, true]].map(([l, v, cp]) => (
                  <div key={l} className="flex justify-between gap-2">
                    <span className={`text-xs ${isDark ? "text-[#888]" : "text-[#999]"}`}>{l}</span>
                    <span className="text-sm font-medium flex items-center gap-1">{v}{cp && <button onClick={() => navigator.clipboard?.writeText(v)}><Copy size={10} className="text-[#CCC]" /></button>}</span>
                  </div>
                ))}
                <p className={`text-xs mt-2 ${isDark ? "text-[#888]" : "text-[#999]"}`}>Bitte innerhalb von {bankInstructions.payment_deadline_days} Tagen überweisen.</p>
              </div>
            </div>
          )}
          {order.guest_tracking_token && (
            <div className={`border ${border} p-4`}>
              <p className={labelCls}>Tracking-Code</p>
              <p className="text-sm font-mono break-all">{order.guest_tracking_token}</p>
              <button onClick={() => navigate("/tracking")} className={`mt-2 text-xs underline ${isDark ? "text-[#C0C0C0]" : "text-[#0A0A0A]"}`}>{t("checkout_extra.track_order")}</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── CHECKOUT FORM (6 Sektionen) ─────────────────────
  const deliveryModes = isGuest
    ? [["SAME_AS_ADDRESS", t("checkout_extra.delivery_same", { defaultValue: "Gleiche Lieferadresse" })], ["PACKSTATION", t("checkout_extra.delivery_pack", { defaultValue: "Packstation" })]]
    : [["SAME_AS_ADDRESS", t("checkout_extra.delivery_same", { defaultValue: "Gleiche Lieferadresse" })], ["PACKSTATION", t("checkout_extra.delivery_pack", { defaultValue: "Packstation" })], ["OTHER_DELIVERY_ADDRESS", t("checkout_extra.delivery_other", { defaultValue: "Abweichende Lieferadresse" })]];

  return (
    <div data-testid="checkout-page" className={`min-h-full ${bg} pb-24`}>
      <div className={`sticky top-0 z-40 px-4 py-3 border-b ${border} ${bg} flex items-center gap-3`}>
        <button data-testid="checkout-back" onClick={() => navigate(-1)}><ArrowLeft size={20} /></button>
        <h1 className="text-base font-bold flex-1">Checkout</h1>
      </div>

      <div className="mx-auto max-w-2xl lg:max-w-3xl xl:max-w-4xl lg:px-6 lg:py-6">

      {error && (
        <InfoCard variant="danger" className="mx-4 mt-3 lg:mx-0" icon={<AlertTriangle size={14} />}>
          {error}
        </InfoCard>
      )}

      {/* Artikel-Info */}
      {isMystery && reservation ? (
        <div data-testid="checkout-mystery-header" className={`${sectionCls}`}>
          <div className="flex items-start gap-3">
            <div className={`w-14 h-18 shrink-0 flex items-center justify-center bg-gradient-to-br from-[#1A1A1A] via-[#0A0A0A] to-[#1A1A1A]`}>
              <Sparkles size={20} className="text-amber-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{reservation.category === "SOCKS" ? "Socken" : "Slips"}-Wundertüte · {reservation.items?.length ?? reservation.item_ids?.length ?? "?"} Artikel</p>
              <p className="text-lg font-bold">{Number(reservation.mb_price).toFixed(2)} {reservation.currency || "EUR"}</p>
              <div className="flex items-center gap-1.5 mt-1 text-[10px] text-amber-500">
                <Timer size={11} />
                <span data-testid="checkout-mystery-countdown">{Math.floor(remainingSec / 60)}:{String(remainingSec % 60).padStart(2, "0")} verbleibend</span>
              </div>
            </div>
          </div>
          {reservation.items?.length > 0 && (
            <div data-testid="checkout-mystery-items" className="mt-3 space-y-1.5">
              {reservation.items.map((it, i) => (
                <div key={it.item_id} data-testid={`checkout-mystery-item-${i}`} className="flex items-center gap-2 py-1 text-[11px]">
                  <div className="w-8 h-8 shrink-0 overflow-hidden bg-[#1A1A1A]">
                    {it.thumb_url && <img src={it.thumb_url} alt="" className={`w-full h-full object-cover ${user?.age_verification_status === "VERIFIED" ? "" : "blur-md opacity-60"}`} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {(reservation.category === "SOCKS" ? "Socken" : "Slip")} · {it.wear_duration_days}T
                      {it.status === "EXPIRED" && <span className="ml-1 text-amber-400">(sonst entsorgt)</span>}
                    </p>
                    <p className={`flex items-center gap-1 ${isDark ? "text-[#888]" : "text-[#999]"}`}>
                      <UserIcon size={9} />
                      {it.seller_display_name || "anonym"}
                    </p>
                  </div>
                  <p className={`font-mono ${isDark ? "text-[#888]" : "text-[#999]"}`}>{Number(it.rescue_price || 0).toFixed(2)} €</p>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className={`${sectionCls} flex items-center gap-3`}>
          <div className={`w-14 h-18 shrink-0 overflow-hidden ${isDark ? "bg-[#1A1A1A]" : "bg-[#F5F5F5]"}`}>
            {listing?.media_urls?.[0] && <img src={listing.media_urls[0]} alt="" className="w-full h-full object-cover" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{listing?.title}</p>
            {/* iter88.25: Sonderwunsch-Zeile inline unter Titel (buyer_message ODER seller_message) */}
            {(negotiation?.buyer_message || negotiation?.seller_message) && (
              <p data-testid="checkout-special-wish-inline" className={`text-[11px] mt-0.5 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                <Sparkles size={10} className="inline mb-0.5 mr-0.5" />
                Sonderwunsch: <span className="italic">{negotiation.buyer_message || negotiation.seller_message}</span>
              </p>
            )}
            <p className="text-lg font-bold">
              {(Number(negotiation?.final_price || listing?.price || 0) + extrasAmount).toFixed(2)} {listing?.currency}
            </p>
            {extrasAmount > 0 && (
              <p className={`text-[10px] mt-0.5 ${isDark ? "text-[#888]" : "text-[#999]"}`} data-testid="checkout-summary-extras-hint">
                inkl. {extrasAmount.toFixed(2)} {listing?.currency || "EUR"} Extras
              </p>
            )}
          </div>
        </div>
      )}

      {/* iter88.25 · Sonderwunsch-Sektion (buyer_message ODER seller_message) */}
      {(negotiation?.buyer_message || negotiation?.seller_message) && (
        <div data-testid="checkout-special-wish-section"
          className={`${sectionCls} border-l-4 border-l-amber-400`}>
          <p className={labelCls}>{t("checkout_extra.special_wish")}</p>
          <div className={`${isDark ? "bg-[#1A1A1A]" : "bg-amber-50"} p-3 rounded-md`}>
            <p className={`text-sm ${isDark ? "text-white" : "text-amber-900"} whitespace-pre-line`} data-testid="checkout-special-wish-text">
              &bdquo;{negotiation.buyer_message || negotiation.seller_message}&ldquo;
            </p>
            <div className={`mt-2 pt-2 border-t ${isDark ? "border-white/10" : "border-amber-200"} text-[11px] ${isDark ? "text-[#AAA]" : "text-amber-800"} flex flex-wrap gap-3`}>
              <span>Vom Verkäufer bestätigt am {negotiation.accepted_at ? new Date(negotiation.accepted_at).toLocaleDateString("de-DE") : "—"}</span>
              {negotiation.final_price != null && (
                <span>{t("checkout_extra.agreed_price")} <strong>{Number(negotiation.final_price).toFixed(2)} EUR</strong></span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SEKTION 1: Name (Klarname · Vor- + Nachname) */}
      <div className={sectionCls}>
        <p className={labelCls}>{t("checkout_extra.step1_name", { defaultValue: "1. Name" })}</p>
        <div className="space-y-2">
          <div className="flex gap-2">
            <input data-testid="checkout-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={`flex-1 min-w-0 ${inputBase}`} placeholder={t("checkout_extra.ph_first_name", { defaultValue: "Vorname *" })} />
            <input data-testid="checkout-last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} className={`flex-1 min-w-0 ${inputBase}`} placeholder={t("checkout_extra.ph_last_name", { defaultValue: "Nachname *" })} />
          </div>
          {isGuest && <input data-testid="checkout-email" type="email" value={buyerEmail} onChange={(e) => setBuyerEmail(e.target.value)} className={inputFull} placeholder={t("checkout_extra.ph_email")} />}
        </div>
      </div>

      {/* SEKTION 2: Adresse */}
      <div className={sectionCls}>
        <p className={labelCls}>{t("checkout_extra.step2_address", { defaultValue: "2. Adresse" })}</p>
        <div className="space-y-2">
          <div className="flex gap-2">
            <StreetAutocomplete
              testId="checkout-bill-street"
              value={billStreet}
              onChange={setBillStreet}
              country={buyerCountry}
              postalCode={billZip}
              locality={billCity}
              className={inputBase}
              wrapperClassName="flex-1 min-w-0"
              placeholder={t("checkout_extra.ph_street")}
            />
            <input data-testid="checkout-bill-nr" value={billHouseNr} onChange={(e) => setBillHouseNr(e.target.value)} className={`w-24 shrink-0 ${inputBase}`} placeholder="Hausnr." />
          </div>
          <div className="flex gap-2">
            <input data-testid="checkout-bill-zip" value={billZip} onChange={(e) => setBillZip(e.target.value)} className={`w-28 shrink-0 ${inputBase}`} placeholder="PLZ *" />
            <input data-testid="checkout-bill-city" value={billCity} onChange={(e) => setBillCity(e.target.value)} className={`flex-1 min-w-0 ${inputBase}`} placeholder="Stadt *" />
          </div>
        </div>
      </div>

      {/* SEKTION 3: Lieferadresse */}
      <div className={sectionCls}>
        <p className={labelCls}>{t("checkout_extra.step3_delivery", { defaultValue: "3. Lieferadresse" })}</p>
        <div className="space-y-3">
          {deliveryModes.map(([code, label]) => (
            <button key={code} data-testid={`delivery-${code}`} onClick={() => setDeliveryMode(code)}
              className={`w-full flex items-center gap-3 p-3 border text-left transition-colors ${deliveryMode === code ? (isDark ? "border-white bg-white/10" : "border-[#0A0A0A] bg-[#FAFAFA]") : border}`}>
              {code === "PACKSTATION" ? <Package size={18} className={isDark ? "text-[#888]" : "text-[#999]"} /> : <MapPin size={18} className={isDark ? "text-[#888]" : "text-[#999]"} />}
              <span className="text-sm">{label}</span>
              {deliveryMode === code && <Check size={16} className="ml-auto text-green-500" />}
            </button>
          ))}
          {deliveryMode === "PACKSTATION" && (
            <div className="space-y-2 pl-3 border-l-2 border-[#E5E5E5] ml-3">
              <input data-testid="pack-nr" value={packNr} onChange={(e) => setPackNr(e.target.value)} className={inputFull} placeholder="Packstationsnummer *" />
              <input data-testid="post-nr" value={postNr} onChange={(e) => setPostNr(e.target.value)} className={inputFull} placeholder="Postnummer (DHL Kundennummer) *" />
              <div className="flex gap-2">
                <input data-testid="pack-zip" value={packZip} onChange={(e) => setPackZip(e.target.value)} className={`w-28 shrink-0 ${inputBase}`} placeholder="PLZ *" />
                <input data-testid="pack-city" value={packCity} onChange={(e) => setPackCity(e.target.value)} className={`flex-1 min-w-0 ${inputBase}`} placeholder="Stadt *" />
              </div>
            </div>
          )}
          {deliveryMode === "OTHER_DELIVERY_ADDRESS" && (
            <div className="space-y-2 pl-3 border-l-2 border-[#E5E5E5] ml-3">
              <div className="flex gap-2">
                <StreetAutocomplete
                  testId="other-street"
                  value={shipStreet}
                  onChange={setShipStreet}
                  country={buyerCountry}
                  postalCode={shipZip}
                  locality={shipCity}
                  className={inputBase}
                  wrapperClassName="flex-1 min-w-0"
                  placeholder={t("checkout_extra.ph_street")}
                />
                <input data-testid="other-house-nr" value={shipHouseNr} onChange={(e) => setShipHouseNr(e.target.value)} className={`w-24 shrink-0 ${inputBase}`} placeholder="Hausnr." />
              </div>
              <div className="flex gap-2">
                <input data-testid="other-zip" value={shipZip} onChange={(e) => setShipZip(e.target.value)} className={`w-28 shrink-0 ${inputBase}`} placeholder="PLZ *" />
                <input data-testid="other-city" value={shipCity} onChange={(e) => setShipCity(e.target.value)} className={`flex-1 min-w-0 ${inputBase}`} placeholder="Stadt *" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* SEKTION 3.5: Versandart · nur bei MARKETPLACE. STX: System-Label immer,
          Buyer zahlt konfigurierbaren Flat-Rate-Versand (siehe Admin STX-Konfig, iter88.18). */}
      {!isMystery && listing?.area_scope === "MARKETPLACE" && labelCatalog.length > 0 && (
        <div className={sectionCls}>
          <p className={labelCls}>Versandart · optional</p>
          <p className={`text-xs mb-2 ${isDark ? "text-[#888]" : "text-[#999]"}`}>
            Wähle ein Label und bezahle den Versand direkt mit. Verkäufer erhält das fertige Label — schneller Versand.
          </p>
          <div className="space-y-2">
            <button
              data-testid="checkout-label-none"
              onClick={() => setSelectedLabel(null)}
              className={`w-full flex items-center gap-3 p-3 border text-left transition-colors ${selectedLabel === null ? (isDark ? "border-white bg-white/10" : "border-[#0A0A0A] bg-[#FAFAFA]") : border}`}>
              <Package size={18} />
              <div className="flex-1">
                <p className="text-sm font-medium">{t("checkout_extra.no_label")}</p>
                <p className={`text-xs ${isDark ? "text-[#888]" : "text-[#999]"}`}>Standardweg (kein Aufpreis)</p>
              </div>
              {selectedLabel === null && <Check size={16} className="text-green-500" />}
            </button>
            {labelCatalog.map((c) => (
              <button
                key={c.carrier}
                data-testid={`checkout-label-${c.carrier}`}
                onClick={() => setSelectedLabel(c)}
                className={`w-full flex items-center gap-3 p-3 border text-left transition-colors ${selectedLabel?.carrier === c.carrier ? (isDark ? "border-white bg-white/10" : "border-[#0A0A0A] bg-[#FAFAFA]") : border}`}>
                <Package size={18} />
                <div className="flex-1">
                  <p className="text-sm font-medium">{c.label}</p>
                  <p className={`text-xs ${isDark ? "text-[#888]" : "text-[#999]"}`}>{c.eta_days} Werktage</p>
                </div>
                <p className="text-sm font-bold">+{c.price.toFixed(2)} €</p>
                {selectedLabel?.carrier === c.carrier && <Check size={16} className="text-green-500 ml-2" />}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* SEKTION 3.6: MP Käuferschutz (iter79 Batch 3) — nur bei Marketplace-Artikeln */}
      {!isMystery && listing?.area_scope === "MARKETPLACE" && (
        <div className={sectionCls}>
          <p className={labelCls}>{t("checkout_extra.buyer_protection_optional")}</p>
          <button
            data-testid="checkout-buyer-protection"
            onClick={() => setBuyerProtectionOptin(!buyerProtectionOptin)}
            className={`w-full flex items-center gap-3 p-3 border text-left transition-colors ${
              buyerProtectionOptin ? (isDark ? "border-white bg-white/10" : "border-[#0A0A0A] bg-[#FAFAFA]") : border
            }`}>
            <div className={`w-5 h-5 border ${border} flex items-center justify-center shrink-0`}>
              {buyerProtectionOptin && <Check size={12} />}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">{t("checkout_extra.activate_buyer_protection")}</p>
              <p className={`text-xs mt-0.5 ${isDark ? "text-[#888]" : "text-[#999]"}`}>
                Sicherer Kauf: Bei Nichterhalt oder Falschartikel bekommst du den Kaufpreis zurück.
              </p>
            </div>
          </button>
        </div>
      )}

      {/* SEKTION 4: Zahlungsart */}
      <div className={sectionCls}>
        <p className={labelCls}>4. Zahlungsart</p>
        {methods.length === 0 ? (
          <InfoCard variant="warning" icon={<AlertTriangle size={16} />}>
            Keine Zahlungsarten konfiguriert.
          </InfoCard>
        ) : (
          <div className="space-y-2">
            {methods.map((m) => (
              <button key={m.code} data-testid={`pay-${m.code}`} onClick={() => setSelectedMethod(m.code)}
                className={`w-full flex items-center gap-3 p-3 rounded-2xl border text-left transition-colors ${selectedMethod === m.code ? (isDark ? "border-white bg-white/10" : "border-[#0A0A0A] bg-[#FAFAFA]") : border}`}>
                {m.code === "PAYPAL" ? <CreditCard size={18} /> : m.code === "STRIPE" ? <CreditCard size={18} /> : <Building2 size={18} />}
                <div className="flex-1">
                  <p className="text-sm font-medium">{m.label}</p>
                  <p className={`text-xs ${isDark ? "text-[#888]" : "text-[#999]"}`}>{m.code === "PAYPAL" ? "Sofort bezahlen" : m.code === "STRIPE" ? "Kreditkarte · Sofort bezahlen (Stripe)" : "Überweisung innerhalb von 7 Tagen"}</p>
                </div>
                {selectedMethod === m.code && <Check size={16} className="text-green-500" />}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* SEKTION 5: Bestellübersicht */}
      <div className={sectionCls}>
        <p className={labelCls}>{t("checkout_extra.step5_summary")}</p>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="min-w-0 truncate pr-2">{isMystery ? "Wundertüte" : `${listing?.title || "Artikel"}${listing?.public_item_no ? ` · Nr. ${listing.public_item_no}` : ""}`}</span>
            <span className="font-medium shrink-0">
              {isMystery ? Number(reservation?.mb_price || 0).toFixed(2) : listing?.price?.toFixed(2)}{" "}
              {isMystery ? (reservation?.currency || "EUR") : listing?.currency}
            </span>
          </div>
          {/* iter88.15c: STX-Extras (Add-Ons + Extra-Tragetage) */}
          {stxAddonRows.map((a) => (
            <div key={a.code} className="flex justify-between" data-testid={`checkout-addon-${a.code}`}>
              <span className={isDark ? "text-[#C0C0C0]" : "text-[#666]"}>Zusatz: {addonLabel(a.code, addonLabels[a.code] || a.code.replace(/^OPT_/, "").replaceAll("_", " ").toLowerCase())}</span>
              <span className="font-medium">+{a.price.toFixed(2)} {listing?.currency || "EUR"}</span>
            </div>
          ))}
          {stxWearRow && (
            <div className="flex justify-between" data-testid={`checkout-wear-${stxWearRow.days}`}>
              <span className={isDark ? "text-[#C0C0C0]" : "text-[#666]"}>+{stxWearRow.days} Tag{stxWearRow.days === 1 ? "" : "e"} zusätzlich</span>
              <span className="font-medium">+{Number(stxWearRow.price).toFixed(2)} {listing?.currency || "EUR"}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span>{t("checkout_extra.shipping")}</span>
            {area === "STILLTOUCHEDX" ? (
              stxShippingCost > 0 ? (
                <span className="font-medium" data-testid="checkout-shipping-cost">
                  +{stxShippingCost.toFixed(2)} EUR
                </span>
              ) : shippingQuote?.free_reason ? (
                <span className={`font-medium ${isDark ? "text-emerald-400" : "text-emerald-600"}`}
                      data-testid="checkout-shipping-free">
                  Kostenlos {shippingQuote.free_reason === "user_group" ? `(${shippingQuote.group_name})` : ""}
                </span>
              ) : (
                <span className={isDark ? "text-[#888]" : "text-[#999]"} data-testid="checkout-shipping-loading">…</span>
              )
            ) : selectedLabel ? (
              <span className="font-medium" data-testid="checkout-label-cost-line">+{selectedLabel.price.toFixed(2)} EUR</span>
            ) : (
              <span className={isDark ? "text-[#888]" : "text-[#999]"}>{t("checkout_extra.direct_by_seller")}</span>
            )}
          </div>
          {!isMystery && listing?.area_scope === "MARKETPLACE" && buyerProtectionOptin && (
            <div className="flex justify-between" data-testid="checkout-protection-line">
              <span>{t("checkout_extra.buyer_protection")}</span>
              <span className="font-medium">+2,49 EUR</span>
            </div>
          )}
          {appliedDiscount && (
            <div className="flex justify-between" data-testid="checkout-discount-line">
              <span className="inline-flex items-center gap-1">Rabatt &bdquo;{appliedDiscount.code}&ldquo;
                <button onClick={() => { setAppliedDiscount(null); setDiscountInput(""); }}
                  className="text-[10px] text-red-500 ml-1" data-testid="checkout-discount-remove">×</button>
              </span>
              <span className={`font-medium ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>−{discountAmount.toFixed(2)} EUR</span>
            </div>
          )}
          {!appliedDiscount && (
            <div className="flex gap-2 pt-1">
              <input
                data-testid="checkout-discount-input"
                value={discountInput}
                onChange={(e) => setDiscountInput(e.target.value.toUpperCase())}
                maxLength={30}
                placeholder="Rabatt-Code"
                className={`flex-1 h-9 px-3 rounded-full text-xs border ${isDark ? "bg-white/5 border-white/10 text-white placeholder:text-[#666]" : "bg-white border-[#E5E5E5] text-[#0A0A0A]"}`} />
              <button
                data-testid="checkout-discount-apply"
                onClick={applyDiscount} disabled={checkingDiscount || !discountInput.trim()}
                className={`h-9 px-4 rounded-full text-xs font-semibold disabled:opacity-40 ${isDark ? "bg-white text-[#0A0A0A]" : "bg-[#0A0A0A] text-white"}`}>
                {checkingDiscount ? "…" : "Einlösen"}
              </button>
            </div>
          )}
          {discountError && (
            <p data-testid="checkout-discount-error" className="text-[11px] text-red-500 pt-1">{discountError}</p>
          )}
          <div className={`flex justify-between pt-2 mt-2 border-t ${border} font-bold`}>
            <span>Gesamt</span>
            <span data-testid="checkout-total">
              {Math.max(0, (
                (isMystery ? Number(reservation?.mb_price || 0) : Number(listing?.price || 0)) +
                extrasAmount +
                stxShippingCost +
                (selectedLabel?.price || 0) +
                ((!isMystery && listing?.area_scope === "MARKETPLACE" && buyerProtectionOptin) ? 2.49 : 0) -
                discountAmount
              )).toFixed(2)}{" "}
              {isMystery ? (reservation?.currency || "EUR") : (listing?.currency || "EUR")}
            </span>
          </div>
        </div>
      </div>

      {/* Pflichtfeld-Hinweis: fehlende Angaben klar benennen */}
      {(() => {
        const missing = [];
        // Profildaten (im Profil pflegbar) getrennt von Checkout-Angaben (Zahlungsart/Lieferung)
        const PROFILE_FIELDS = ["Vorname", "Nachname", "Straße", "Hausnummer", "PLZ", "Stadt"];
        if (!firstName.trim()) missing.push("Vorname");
        if (!lastName.trim()) missing.push("Nachname");
        if (isGuest && !buyerEmail.trim()) missing.push("E-Mail-Adresse");
        if (!billStreet.trim()) missing.push("Straße");
        if (!billHouseNr.trim()) missing.push("Hausnummer");
        if (!billZip.trim()) missing.push("PLZ");
        if (!billCity.trim()) missing.push("Stadt");
        if (deliveryMode === "PACKSTATION" && (!packNr || !postNr || !packZip || !packCity)) missing.push("Packstation-Angaben");
        if (deliveryMode === "OTHER_DELIVERY_ADDRESS" && (!shipStreet || !shipHouseNr || !shipZip || !shipCity)) missing.push("abweichende Lieferadresse");
        if (!selectedMethod) missing.push("Zahlungsart");
        if (missing.length === 0) return null;
        // Profil-Link nur zeigen, wenn wirklich Profildaten (Name/Adresse) fehlen —
        // Zahlungsart & Lieferung werden hier im Checkout gewählt, nicht im Profil.
        const profileMissing = missing.filter((m) => PROFILE_FIELDS.includes(m));
        return (
          <div className="px-4 lg:px-0 pb-1">
            <InfoCard variant="warning" icon={<AlertTriangle size={14} />} testId="checkout-missing-fields">
              <p className="font-medium mb-1">{t("checkout_extra.complete_data_prompt")}</p>
              <p>{missing.join(" · ")}</p>
              {!isGuest && profileMissing.length > 0 && (
                <button
                  data-testid="checkout-complete-profile"
                  onClick={() => navigate("/profile/edit")}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-semibold underline"
                >
                  Jetzt im Profil vervollständigen →
                </button>
              )}
            </InfoCard>
          </div>
        );
      })()}

      {/* SEKTION 6: Kauf abschließen */}
      <div className="px-4 py-4 lg:px-0">
        <button data-testid="checkout-submit" onClick={submitOrder} disabled={processing || !canSubmit()}
          className={`w-full h-12 rounded-full text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-30 active:scale-[0.98] ${isDark ? "bg-white text-[#0A0A0A]" : "bg-[#0A0A0A] text-white"}`}>
          {processing ? <><Loader2 size={16} className="animate-spin" /> Wird verarbeitet...</> :
           <><ShoppingBag size={16} /> {selectedMethod === "PAYPAL" ? "Mit PayPal bezahlen" : selectedMethod === "STRIPE" ? "Mit Kreditkarte bezahlen" : "Kostenpflichtig bestellen"}</>}
        </button>
      </div>
      </div>
    </div>
  );
}
